import React, { useMemo } from 'react'
import cx from 'classnames'
import { pathOr } from 'ramda'
import { subscribe } from 'react-contextual'
import { SearchFilterProvider } from 'views/providers'
import ContactsProvider from '../../state/ContactsProvider'
import {
  OUTSTANDING_GROUP_KEY,
  TFilterableContact,
  applyContactFilterPills,
  countContactsForPill,
  getContactFilterGroups,
} from '../../filters'
import { useContactFilterPills } from '../../useContactFilterPills'
import styles from './ContactFilterPills.module.scss'

type TProps = {
  filters: Array<{ key: string; contacts: TFilterableContact[] }>
  activeFilterIndex: number
}

const ContactFilterPills = ({ filters, activeFilterIndex }: TProps): React.ReactElement => {
  const { selection, togglePill } = useContactFilterPills()

  const contacts = pathOr<TFilterableContact[]>([], [activeFilterIndex, 'contacts'], filters)
  const tabKey = pathOr<string>('', [activeFilterIndex, 'key'], filters)

  const groups = useMemo(
    () =>
      getContactFilterGroups(tabKey)
        .map(group => {
          const countableContacts = applyContactFilterPills(contacts, selection, {
            tabKey,
            excludeGroupKey: group.key,
          })

          return {
            ...group,
            pills: group.pills
              .map(pill => ({
                ...pill,
                count: countContactsForPill(countableContacts, pill),
                isActive: selection[group.key] === pill.key,
              }))
              .filter(pill => group.key !== OUTSTANDING_GROUP_KEY || pill.count > 0 || pill.isActive),
          }
        })
        .filter(group => group.pills.length > 0),
    [contacts, selection, tabKey],
  )

  return (
    <div className={styles.root}>
      {groups.map(group => (
        <div className={styles.group} key={group.key}>
          <span className={styles.label} id={`contact-filter-${group.key}`}>
            {group.label}
          </span>
          <ul className={styles.pills} aria-labelledby={`contact-filter-${group.key}`}>
            {group.pills.map(pill => (
              <li key={pill.key}>
                <button
                  type="button"
                  aria-pressed={pill.isActive}
                  className={cx(styles.pill, { [styles.active]: pill.isActive })}
                  onClick={() => togglePill(group.key, pill.key)}
                >
                  {pill.label}
                  <span className={styles.count}>{pill.count}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

export default subscribe([ContactsProvider, SearchFilterProvider], ({ filters }: any, { activeFilterIndex }: any) => ({
  filters,
  activeFilterIndex,
}))(ContactFilterPills)
